import React, { useContext, useCallback, useMemo, useRef } from 'react'
import { Formik, FormikProps, Form } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { showSnackBar } from 'services/toast'
import OnFormikValidationError from 'components/OnFormikValidationError'
import { createGetRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    CampaignsActions,
    updateCampaign,
    CAMPAIGN_STATUS_ENUM,
    CampaignState,
    publishCampaign,
    submitCampaign,
} from 'state/campaigns'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from '../..'
import CampaignObjective from './CampaignObjective'
import SaveAndSubmitButtonGroup from 'components/Campaign/Editor/SaveAndSubmitButtonGroup'
import { selectorEditorErrors, SET_UP_ERRORS } from 'state/error'
import { ERROR_MESSAGES } from './errorMessages'
import { runValidateSchema, validateFieldsAreAllPassed, setInitialTouched } from 'components/utils'
import _, { noop } from 'lodash'
import { baseValidationSchema } from './baseValidationSchema'
import MaskWrapper from 'components/Campaign/Editor/MaskWrapper'
import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { usePermission } from 'hooks/permissions'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import BrandDropdown from 'components/Campaign/BrandDropdown'
import { useSelectedBrand } from 'components/Campaign/BrandDropdown/subscriber'
import { fieldIdentifiers } from 'components/Shared/Comment'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'

export interface FormValues {
    brandUuid?: string
    objective?: number | null
    categories?: number[]
    title?: string
    budget?: number | null
    registration_enable?: boolean
    registration_max_slots?: number | null
    isCampaignLive: boolean
    is_private: boolean
    batching_enable: boolean
    submission_status_filling_fast_min?: number | null
    submission_status_full_min?: number | null
}

interface WrapperProps {
    campaign?: CampaignState
}

export const Wrapper: React.FC<WrapperProps> = ({ campaign }) => {
    const isPublish = useRef(true)
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const viewType = useContext(ViewTypeContext)
    const isUpdating = useSelector(createGetRequestsAreLoading([STATE_KEYS.UpdateCampaign]))
    const errorTypes = [...Object.values(SET_UP_ERRORS), 'registration']
    const errors = useSelector(selectorEditorErrors(errorTypes))
    const [selectedBrand] = useSelectedBrand()

    const isAbleToReview =
        usePermission(CampaignStatusControl.ReviewCampaign) && campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isAbleToLaunch =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)

    const formSchema = useMemo(() => {
        const isCampaignDraftOrForEdit = campaign
            ? [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(campaign.status)
            : true
        const lastEditSlots =
            campaign && typeof campaign.registration_max_slots === 'number' ? campaign.registration_max_slots : 0
        return baseValidationSchema(isCampaignDraftOrForEdit, lastEditSlots)
    }, [campaign])

    const formErrors = useMemo(() => {
        if (!errors) return null
        const returnErrors: any = _.cloneDeep(errors)

        if (errors) {
            if (errors.registration) {
                if (errors.registration.max_slots) {
                    returnErrors['registration_max_slots'] = errors.registration.max_slots
                }
                delete returnErrors.registration
            }
        }
        Object.keys(returnErrors).forEach((key: string) => {
            if (!returnErrors[key]) return
            if (ERROR_MESSAGES[key] && ERROR_MESSAGES[key][returnErrors[key]]) {
                if (key === SET_UP_ERRORS.BRAND) {
                    returnErrors['brandUuid'] = ERROR_MESSAGES[key][returnErrors[key]]
                    delete returnErrors[SET_UP_ERRORS.BRAND]
                } else {
                    returnErrors[key] = ERROR_MESSAGES[key][returnErrors[key]]
                }
            }
        })
        return returnErrors
    }, [errors])

    const initialTouched = useMemo(() => {
        const errors = _.cloneDeep(formErrors)
        if (errors) {
            return setInitialTouched(errors)
        } else {
            return {}
        }
    }, [formErrors])

    const validate: any = useCallback(
        async (values: any) => {
            const schemaErrors = await runValidateSchema(values, formSchema)
            if (formErrors && isPublish.current) {
                const returnError = _.cloneDeep(formErrors)
                if (returnError[SET_UP_ERRORS.OBJECTIVE] && values.objective) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.OBJECTIVE]) {
                        returnError[SET_UP_ERRORS.OBJECTIVE] = schemaErrors[SET_UP_ERRORS.OBJECTIVE]
                    } else {
                        returnError[SET_UP_ERRORS.OBJECTIVE] = ''
                    }
                }
                if (returnError[SET_UP_ERRORS.CATEGORIES] && values.categories.length > 0) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.CATEGORIES]) {
                        returnError[SET_UP_ERRORS.CATEGORIES] = schemaErrors[SET_UP_ERRORS.CATEGORIES]
                    } else {
                        returnError[SET_UP_ERRORS.CATEGORIES] = ''
                    }
                }
                if (returnError[SET_UP_ERRORS.BUDGET] && values.budget) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.BUDGET]) {
                        returnError[SET_UP_ERRORS.BUDGET] = schemaErrors[SET_UP_ERRORS.BUDGET]
                    } else {
                        returnError[SET_UP_ERRORS.BUDGET] = ''
                    }
                }
                if (returnError['registration_max_slots'] && values.registration_max_slots) {
                    if (schemaErrors && schemaErrors['registration_max_slots']) {
                        returnError['registration_max_slots'] = schemaErrors['registration_max_slots']
                    } else {
                        returnError['registration_max_slots'] = ''
                    }
                }
                if (returnError[SET_UP_ERRORS.SLOT_NUMBER] && values.slot_number && values.slot_required) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.SLOT_NUMBER]) {
                        returnError[SET_UP_ERRORS.SLOT_NUMBER] = schemaErrors[SET_UP_ERRORS.SLOT_NUMBER]
                    } else {
                        returnError[SET_UP_ERRORS.SLOT_NUMBER] = ''
                    }
                } else if (!values.slot_required && !values.slot_number) {
                    // if slot_required is false, slot_number is not required and remove the error message
                    returnError[SET_UP_ERRORS.SLOT_NUMBER] = ''
                }
                if (
                    returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FILLING_FAST_MIN] &&
                    values.submission_status_filling_fast_min
                ) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.SUBMISSION_STATUS_FILLING_FAST_MIN]) {
                        returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FILLING_FAST_MIN] =
                            schemaErrors[SET_UP_ERRORS.SUBMISSION_STATUS_FILLING_FAST_MIN]
                    } else {
                        returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FILLING_FAST_MIN] = ''
                    }
                }
                if (returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FULL_MIN] && values.submission_status_full_min) {
                    if (schemaErrors && schemaErrors[SET_UP_ERRORS.SUBMISSION_STATUS_FULL_MIN]) {
                        returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FULL_MIN] =
                            schemaErrors[SET_UP_ERRORS.SUBMISSION_STATUS_FULL_MIN]
                    } else {
                        returnError[SET_UP_ERRORS.SUBMISSION_STATUS_FULL_MIN] = ''
                    }
                }

                if (returnError['brandUuid'] && values.brandUuid) {
                    if (schemaErrors && schemaErrors['brandUuid']) {
                        returnError['brandUuid'] = schemaErrors['brandUuid']
                    } else {
                        returnError['brandUuid'] = ''
                    }
                }

                const isAllFieldsPassed = validateFieldsAreAllPassed(returnError)
                if (isAllFieldsPassed) {
                    return {}
                } else {
                    return returnError
                }
            } else {
                if (schemaErrors) {
                    return schemaErrors
                } else {
                    return {}
                }
            }
        },
        [formErrors, formSchema],
    )

    return (
        <Formik
            initialValues={{
                brandUuid: campaign ? campaign.brand.uuid : undefined,
                objective: campaign ? campaign.objective : null,
                categories: campaign ? campaign.categories : [],
                title: campaign ? campaign.title : '',
                budget: campaign ? campaign.budget : null,
                slot_required: campaign?.slot_required ?? true,
                slot_number: campaign?.slot_number ?? null,
                isCampaignLive: campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE ? true : false,
                is_private: campaign && campaign.is_private ? campaign.is_private : false,
                batching_enable: campaign ? campaign.batching_enable : false,
                registration_enable: campaign ? campaign.registration_enable : false,
                registration_max_slots: campaign ? campaign.registration_max_slots : null,
                auto_update_submission_status: campaign ? campaign.auto_update_submission_status : false,
                submission_status_filling_fast_min: campaign ? campaign.submission_status_filling_fast_min : null,
                submission_status_full_min: campaign ? campaign.submission_status_full_min : null,
            }}
            initialErrors={formErrors}
            validate={validate}
            enableReinitialize
            initialTouched={initialTouched}
            onSubmit={
                viewType === EDITOR_ROUTE_ENUM.EDIT
                    ? (values, actions): void => {
                          const {
                              brandUuid,
                              auto_update_submission_status,
                              batching_enable,
                              budget,
                              categories,
                              is_private,
                              objective,
                              registration_enable,
                              registration_max_slots,
                              submission_status_filling_fast_min,
                              submission_status_full_min,
                              title,
                          } = values
                          const dataToUpdate = {
                              brand: { uuid: brandUuid },
                              auto_update_submission_status,
                              batching_enable,
                              budget,
                              slot_number: values.slot_number,
                              slot_required: values.slot_required,
                              categories,
                              is_private,
                              objective,
                              registration_enable,
                              registration_max_slots,
                              submission_status_filling_fast_min,
                              submission_status_full_min,
                              title,
                          }
                          if (campaign) {
                              if (isPublish.current) {
                                  if (isAbleToLaunch) {
                                      dispatch(
                                          publishCampaign({
                                              data: {
                                                  campaign: dataToUpdate as any,
                                                  uuid: campaign.uuid,
                                              },
                                          }),
                                      )
                                  } else {
                                      dispatch(
                                          submitCampaign({
                                              data: {
                                                  campaign: dataToUpdate as any,
                                                  uuid: campaign.uuid,
                                              },
                                          }),
                                      )
                                  }
                              } else {
                                  //save changes
                                  dispatch(
                                      updateCampaign({
                                          campaign: dataToUpdate as any,
                                          uuid: campaign.uuid,
                                      }),
                                  )
                              }
                          } else {
                              // save and create campaign
                              dispatch({ type: CampaignsActions.REGISTER_CAMPAIGN_REQUEST, data: dataToUpdate })
                          }
                          actions.setSubmitting(false)
                          localStorage.removeItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
                      }
                    : noop
            }
        >
            {(formikBag: FormikProps<FormValues>): JSX.Element => {
                return (
                    <CompareChangesPopup
                        initialData={formikBag.initialValues}
                        compareData={formikBag.values}
                        recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                    >
                        <MaskWrapper>
                            <Form>
                                <div className="form-field">
                                    <OnFormikValidationError
                                        callback={(): void => {
                                            if (!isPublish.current) return
                                            showSnackBar({
                                                title: t('errorMessage.validation.enterDetailsRedSubtext'),
                                                type: 'error',
                                            })
                                        }}
                                    />
                                    <div className="brands-list">
                                        <CampaignEditorInputFieldContainer>
                                            <BrandDropdown
                                                campaignBrand={campaign ? campaign.brand : undefined}
                                                isSetInitSearch={false}
                                                identifier={fieldIdentifiers.brandName}
                                            />
                                        </CampaignEditorInputFieldContainer>
                                    </div>
                                    <CampaignObjective
                                        campaignStatus={campaign && campaign.status}
                                        campaignBrand={selectedBrand || undefined}
                                    />
                                </div>

                                <EditCampaignRestricted
                                    campaignStatus={campaign && campaign.status}
                                    isAbleToReview={isAbleToReview}
                                    isAbleToLaunch={isAbleToLaunch}
                                >
                                    <SaveAndSubmitButtonGroup
                                        campaign={campaign}
                                        disableSave={isUpdating}
                                        disableSubmit={isUpdating}
                                        onSubmit={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = true
                                            formikBag.submitForm()
                                        }}
                                        onSave={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = false
                                            formikBag.submitForm()
                                        }}
                                    />
                                </EditCampaignRestricted>
                            </Form>
                        </MaskWrapper>
                    </CompareChangesPopup>
                )
            }}
        </Formik>
    )
}
